import React from 'react';
import { FormField } from '../../components/Form/FormField';
import { FAIcon } from '../../components/fa-icon';
import { formatClassName } from '../../utils';

interface Props {
  // `children` stays `any` (was PropTypes.any): renderField reads handlers off the single child
  children?: any;
  className?: string;
  as?: string;
  // `dataValue` stays `any` (was PropTypes.any)
  dataValue?: any;
  disabled?: boolean;
  inlineStacked?: boolean;
  isFloatOutside?: boolean;
  labelClassName?: string;
  // handler values stay `any`: legacy form controls call back with varying value shapes
  onChange?: (value?: any) => void;
  onBlur?: (value?: any) => void;
  populatedOverride?: boolean;
  searchField?: boolean;
  tooltip?: string;
  // remaining props are spread through to FormField untyped (library-boundary any)
  [key: string]: any;
}

interface State {
  toggled: boolean;
  populated: boolean;
}

export class FloatingLabelFormField extends React.Component<Props, State> {
  static defaultProps = {
    disabled: false,
    inlineStacked: false,
  };

  // handlers/metadata captured off the single child in renderField (legacy pattern, untyped)
  childBlur?: any;
  childChange?: any;
  childFocus?: any;
  childType?: any;
  fieldClassName?: any;

  constructor(props: Props) {
    super(props);
    const populated = props.dataValue !== undefined && props.dataValue !== null && props.dataValue !== '';
    this.state = {
      toggled: false,
      populated,
    };
  }

  onFocus = () => {
    if (typeof this.childFocus === 'function') {
      this.childFocus();
    }
    this.setState({ toggled: true });
  };

  onChange = (value?: any) => {
    if (typeof this.childChange === 'function') {
      this.childChange(value);
    }
    if (typeof this.props.onChange === 'function') {
      this.props.onChange(value);
    }
    this.setPopulated(value);
  };

  onBlur = (value?: any) => {
    if (typeof this.childBlur === 'function') {
      this.childBlur(value);
    }
    if (typeof this.props.onBlur === 'function') {
      this.props.onBlur(value);
    }
    this.setPopulated(value);
    this.setState({ toggled: false });
  };

  onDataLoaded = (value?: any) => {
    this.setPopulated(value);
  };

  setPopulated = (value?: any) => {
    let present = !!value;
    if (value instanceof Array) {
      present = value.length > 0;
    }
    if (this.state.populated !== present) {
      this.setState({ populated: present });
    }
  };

  renderField = () => {
    if (this.props.children) {
      const child = React.Children.only(this.props.children);
      this.childBlur = child.props.onBlur;
      this.childChange = child.props.onChange;
      this.childFocus = child.props.onFocus;
      this.childType = child.type;
      this.fieldClassName = child.props.fieldClassName;
      return React.cloneElement(child, {
        onBlur: this.onBlur,
        onChange: this.onChange,
        onFocus: this.onFocus,
        onDataLoaded: this.onDataLoaded,
      });
    }
  };

  render() {
    const { as, className, disabled, inlineStacked, labelClassName, populatedOverride, searchField, tooltip } =
      this.props;
    const { populated, toggled } = this.state;

    const wrapperClassName = formatClassName([
      'fg-line',
      'ui-default',
      'regular',
      as,
      this.fieldClassName,
      { 'fg-disabled': disabled },
      { 'fg-populated': !populatedOverride && populated },
      { 'fg-toggled': toggled },
      // `!!` is compile-time only coercion: formatClassName evaluates the value's truthiness either way
      { 'fg-tooltip': !!tooltip },
    ]);

    const labelClass = formatClassName([labelClassName, 'fg-label']);
    const fieldClass = formatClassName([className, { 'fg-float': !inlineStacked }]);

    // single spread-cast object (library-boundary cast, Pilot pattern): FormField's strict props
    // don't declare onChange / a null searchField, but this legacy component has always passed
    // them through at runtime — merge order matches the original spread + explicit attributes
    return (
      <FormField
        {...({
          ...this.props,
          className: fieldClass,
          onBlur: this.onBlur,
          onChange: this.onChange,
          onFocus: this.onFocus,
          inputClassName: 'fg-input',
          tooltip,
          searchField: searchField ? <FAIcon icon="search" /> : null,
          labelClassName: labelClass,
          wrapperClassName,
          renderAsLegacy: true,
        } as any)}
      >
        {this.renderField()}
      </FormField>
    );
  }
}
